import type { Session } from './types'
import { verificationLive } from './types'
import { artifactRevision, type ExperimentSpec } from './experiments'
import { canonicalSemanticJson } from './semanticGit'
import { fnv1a64 } from './similarity'
import {
  assessMathematicalResearch,
  mathHeads,
  sameMathRef,
  diagnosticPlanIssue,
  diagnosticPriority,
  repairIssues,
  checkMathRegression,
  type MathContext,
  type MathematicalResearch,
} from './mathematics'

/** Preparing the environment has its own native execution binding. This
 * fingerprint owns the scientific inputs, including all consumed revisions. */
export const mathematicalExperimentRevision = (spec: ExperimentSpec): string =>
  fnv1a64(canonicalSemanticJson({ ...spec, environment: null }))

export function mathematicalExperimentPlan(session: Session, experimentId: string) {
  const state = session.research?.mathematics
  if (!state) return undefined
  const spec = session.research?.experiments?.experiments.find((e) => e.id === experimentId)
  const context = mathematicalContextFor(session)
  const eligible = (plan: (typeof state.plans)[number]) =>
    !!spec &&
    plan.experimentRevision === mathematicalExperimentRevision(spec) &&
    state.obligations.some(
      (o) => sameMathRef(o, plan.target) && o.nodeId === spec.context.nodeId,
    ) &&
    !diagnosticPlanIssue(state, plan, context)
  return mathHeads(state.plans)
    .filter((p) => p.experimentId === experimentId)
    .sort(
      (a, b) =>
        Number(eligible(b)) - Number(eligible(a)) ||
        Number(sameMathRef(b.target, state.focus)) - Number(sameMathRef(a.target, state.focus)) ||
        diagnosticPriority(a.role) - diagnosticPriority(b.role) ||
        b.createdAt - a.createdAt ||
        a.id.localeCompare(b.id),
    )[0]
}

export function mathematicalExperimentIssue(session: Session, experimentId: string): string | null {
  if (session.research?.mathematicsInvalid)
    return 'Repair the mathematical workspace before executing an experiment.'
  const state = session.research?.mathematics
  if (!state?.enabled) return null
  const plan = mathematicalExperimentPlan(session, experimentId)
  if (!plan)
    return 'Register the experiment outcomes against an exact proof obligation in Mathematics first.'
  const issue = diagnosticPlanIssue(state, plan, mathematicalContextFor(session))
  if (issue) return issue
  const spec = session.research?.experiments?.experiments.find((e) => e.id === experimentId)
  const target = state.obligations.find((o) => sameMathRef(o, plan.target))
  if (
    !spec ||
    spec.context.nodeId !== target?.nodeId ||
    plan.experimentRevision !== mathematicalExperimentRevision(spec)
  )
    return 'The experiment contract differs from its diagnostic plan. Review its inputs and register a new plan revision.'
  return null
}

export function mathematicalContextFor(session: Session): MathContext {
  const sourceRevisions: Record<string, string> = {}
  for (const node of Object.values(session.nodes))
    if (verificationLive(node))
      sourceRevisions[node.id] = fnv1a64(canonicalSemanticJson(node.verification))
  const artifacts = session.research?.experiments?.artifacts ?? []
  const privateIds = new Set(artifacts.filter((a) => a.kind === 'evaluator').map((a) => a.id))
  for (const spec of session.research?.experiments?.experiments ?? [])
    privateIds.add(spec.evaluatorDataId)
  let changed = true
  while (changed) {
    changed = false
    for (const artifact of artifacts)
      if (!privateIds.has(artifact.id) && artifact.dependsOn.some((r) => privateIds.has(r.id))) {
        privateIds.add(artifact.id)
        changed = true
      }
  }
  return {
    sourceRevisions,
    artifactRevisions: Object.fromEntries(
      artifacts.filter((a) => !privateIds.has(a.id)).map((a) => [a.id, artifactRevision(a)]),
    ),
  }
}

/** Supply exact relevant definitions, including reusable theorem translations.
 * Ancestors are proof references, not exploratory parent edges. No private
 * evaluator bytes enter through artifact joins. */
export function mathematicalActionContext(session: Session, targetIds: readonly string[]) {
  if (session.research?.mathematicsInvalid)
    throw new Error('Repair the invalid mathematical workspace before another research action.')
  const state = session.research?.mathematics
  if (!state?.enabled) return null
  const ids = new Set(
    mathHeads(state.obligations)
      .filter((o) => targetIds.includes(o.nodeId))
      .map((o) => o.id),
  )
  if (!ids.size && state.focus) ids.add(state.focus.id)
  const expand = (historical: boolean) => {
    let changed = true
    const rows = historical ? state.obligations : mathHeads(state.obligations)
    while (changed) {
      changed = false
      const add = (id: string) => {
        if (!ids.has(id)) {
          ids.add(id)
          changed = true
        }
      }
      for (const o of rows.filter((o) => ids.has(o.id))) for (const r of o.requires) add(r.id)
      for (const e of state.evidence.filter(
        (e) => ids.has(e.target.id) && rows.some((o) => sameMathRef(o, e.target)),
      ))
        if (e.kind === 'exact-certificate' && e.interpretation.obligation)
          add(e.interpretation.obligation.id)
        else if (e.kind === 'theorem-application')
          for (const m of e.mappings) if (m.via.kind === 'obligation') add(m.via.ref.id)
    }
  }
  expand(false)
  const currentIds = new Set(ids)
  // A comparison packet carries old revisions too. Their exact prerequisites
  // must travel with them even when the latest revision no longer uses them.
  // Historical material does not become a current-input veto by being included.
  expand(true)
  const obligations = state.obligations.filter((o) => ids.has(o.id))
  const evidence = state.evidence.filter((e) => ids.has(e.target.id))
  const packageIds = new Set(obligations.map((o) => o.package.id))
  const theoremIds = new Set(
    evidence.flatMap((e) => (e.kind === 'theorem-application' ? [e.theorem.id] : [])),
  )
  const workspace: MathematicalResearch = {
    ...state,
    obligations,
    packages: state.packages.filter((p) => packageIds.has(p.id)),
    evidence,
    theorems: state.theorems.filter((t) => theoremIds.has(t.id)),
    relationships: state.relationships.filter((r) => ids.has(r.from.id) || ids.has(r.to.id)),
    plans: state.plans.filter((p) => ids.has(p.target.id)),
    regressions: state.regressions.filter((r) => packageIds.has(r.package.id)),
  }
  const context = mathematicalContextFor(session)
  // Historical definitions remain in the packet for comparison. Their old
  // artifact fingerprints must not veto a newly reviewed, current revision.
  const currentPackages = mathHeads(workspace.obligations)
    .filter((o) => currentIds.has(o.id))
    .map((o) => workspace.packages.find((p) => sameMathRef(p, o.package)))
  if (
    currentPackages.some(
      (p) =>
        !p ||
        !sameMathRef(
          p,
          mathHeads(workspace.packages).find((row) => row.id === p.id),
        ) ||
        p.artifacts.some((r) => context.artifactRevisions?.[r.id] !== r.revision),
    )
  )
    throw new Error(
      'A mathematical input is private, changed or unavailable. Resolve it before sending this research package.',
    )
  return { workspace, context }
}

export function mathematicalProgressFor(session: Session) {
  const state = session.research?.mathematics
  return state ? assessMathematicalResearch(state, mathematicalContextFor(session)) : null
}

export function mathematicalProgressText(session: Session): string[] {
  const state = session.research?.mathematics
  if (!state)
    return session.research?.mathematicsInvalid
      ? ['Mathematical records require repair; execution is blocked.']
      : []
  const p = mathematicalProgressFor(session)!
  return [
    `${p.usable} usable mathematical obligations; ${p.counterexamples} checked counterexamples; ${p.mechanismsEliminated} eliminated proof mechanisms.`,
    `${p.reusedDependencies} usable dependency joins; ${p.theoremApplications} reusable theorem applications.`,
    `${state.packages.length} retained conjecture revisions; ${state.obligations.length} retained obligation revisions; ${state.evidence.length} evidence and review records.`,
    'An agenda frontier stop means no unattempted action met the current mechanical eligibility rules; it does not mean mathematical possibilities were exhausted.',
    ...p.obligations.map(
      (row) =>
        `${row.obligation.title} (${row.obligation.revision}): ${row.status}. ${row.reasons.join(' ')}`,
    ),
    ...p.relationships
      .filter((r) => r.mechanismEliminated)
      .map(
        (r) =>
          `Mechanism ${r.from.id} was eliminated. The target ${r.to.id} is assessed separately.`,
      ),
    ...mathHeads(state.packages).flatMap((pkg) =>
      repairIssues(state, pkg, mathematicalContextFor(session)).map(
        (issue) => `Repair ${pkg.title} (${pkg.revision}) remains incomplete: ${issue}`,
      ),
    ),
    ...state.regressions.map((r) => {
      const checked = checkMathRegression(state, r)
      return `Regression ${r.id} for package ${r.package.revision}: ${checked.passed ? 'passed' : 'failed'}. ${checked.reason}`
    }),
    ...(state.focus && !p.obligations.some((o) => sameMathRef(o.obligation, state.focus))
      ? ['The focused obligation was superseded; choose its current revision explicitly.']
      : []),
    'Operation completion is not proof completion. Recorded human reviews are not authenticated identities or formal certification. Exact certificates establish only their checked inputs under the recorded interpretation.',
  ]
}
